import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Watch, Activity, Heart, Moon, Flame, Footprints, 
  TrendingUp, TrendingDown, Minus, RefreshCw, Link2, 
  Unlink, Smartphone, Circle, Zap, Droplets, Brain,
  ChevronRight, CheckCircle2, AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const DEVICE_ICONS = {
  apple_watch: Watch,
  fitbit: Activity,
  garmin: Watch,
  google_fit: Smartphone,
  oura: Circle,
  samsung: Smartphone,
  whoop: Activity
};

const DEVICE_COLORS = {
  apple_watch: 'from-gray-700 to-gray-900',
  fitbit: 'from-teal-500 to-cyan-600',
  garmin: 'from-blue-600 to-indigo-700',
  google_fit: 'from-green-500 to-emerald-600',
  oura: 'from-purple-500 to-violet-600',
  samsung: 'from-blue-500 to-blue-700',
  whoop: 'from-yellow-500 to-orange-600'
};

const WearablesHub = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [supportedDevices, setSupportedDevices] = useState([]);
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState({});
  const [deviceTrends, setDeviceTrends] = useState({});
  const [deviceAnalytics, setDeviceAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [selectedTrendDevice, setSelectedTrendDevice] = useState('all');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (connectedDevices.length > 0) {
      loadAnalytics();
      loadDeviceAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, connectedDevices.length]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [devicesRes, connectedRes] = await Promise.all([
        axios.get(`${API}/wearables/supported-devices`),
        axios.get(`${API}/wearables/devices`)
      ]);
      setSupportedDevices(devicesRes.data.devices);
      setConnectedDevices(connectedRes.data.devices);
      
      if (connectedRes.data.devices.length > 0) {
        await loadAnalytics();
        await loadTrends();
        await loadDeviceTrends();
        await loadDeviceAnalytics();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/wearables/analytics?period=${selectedPeriod}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadDeviceAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/wearables/analytics-by-device?period=${selectedPeriod}`);
      setDeviceAnalytics(response.data);
    } catch (error) {
      console.error('Error loading device analytics:', error);
    }
  };

  const loadTrends = async () => {
    try {
      const metricTypes = ['heart_rate', 'steps', 'sleep', 'hrv'];
      const trendData = {};
      
      for (const metric of metricTypes) {
        const response = await axios.get(`${API}/wearables/trends/${metric}?days=30`);
        trendData[metric] = response.data.trend;
      }
      
      setTrends(trendData);
    } catch (error) {
      console.error('Error loading trends:', error);
    }
  };

  const loadDeviceTrends = async () => {
    try {
      const metricTypes = ['heart_rate', 'steps', 'sleep', 'hrv'];
      const trendData = {};
      
      for (const metric of metricTypes) {
        const response = await axios.get(`${API}/wearables/trends-by-device/${metric}?days=30`);
        trendData[metric] = response.data.devices;
      }
      
      setDeviceTrends(trendData);
    } catch (error) {
      console.error('Error loading device trends:', error);
    }
  };

  const connectDevice = async (deviceType) => {
    setConnecting(deviceType);
    try {
      // First check if real integration is available
      const statusRes = await axios.get(`${API}/wearables/supported-devices`);
      const isRealAvailable = statusRes.data?.is_real_available;
      
      if (isRealAvailable) {
        // Initiate OAuth flow for real integration
        const authRes = await axios.post(`${API}/wearables/auth/init`, {
          device_type: deviceType,
          redirect_uri: `${window.location.origin}/wearables`
        });
        
        if (authRes.data.is_mock) {
          // Fallback to instant mock connection
          const response = await axios.post(`${API}/wearables/connect`, {
            device_type: deviceType
          });
          toast.success(response.data.message);
          setShowConnectDialog(false);
          await loadData();
        } else {
          // Redirect to OAuth URL for real integration
          toast.info('Redirecting to authorize your device...');
          window.location.href = authRes.data.auth_url;
        }
      } else {
        // Use mock connection (instant)
        const response = await axios.post(`${API}/wearables/connect`, {
          device_type: deviceType
        });
        toast.success(response.data.message);
        setShowConnectDialog(false);
        await loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect device');
    } finally {
      setConnecting(null);
    }
  };

  const disconnectDevice = async (deviceType) => {
    try {
      await axios.delete(`${API}/wearables/devices/${deviceType}`);
      toast.success('Device disconnected');
      await loadData();
    } catch (error) {
      toast.error('Failed to disconnect device');
    }
  };

  const syncDevice = async (deviceType) => {
    setSyncing(deviceType);
    try {
      const response = await axios.post(`${API}/wearables/sync/${deviceType}`);
      toast.success(`Synced ${response.data.new_data_points} data points`);
      await loadAnalytics();
      await loadTrends();
    } catch (error) {
      toast.error('Failed to sync device');
    } finally {
      setSyncing(null);
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getHealthScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  const MetricCard = ({ title, value, unit, icon: Icon, trend, color, subtitle }) => (
    <Card className="hover:shadow-lg transition-all">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold">{value}</span>
              <span className="text-gray-500 text-sm">{unit}</span>
            </div>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-gradient-to-br ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center gap-1 mt-3 text-sm ${
            trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-500'
          }`}>
            {trend > 0 ? <TrendingUp className="w-4 h-4" /> : 
             trend < 0 ? <TrendingDown className="w-4 h-4" /> : 
             <Minus className="w-4 h-4" />}
            <span>{Math.abs(trend)}% vs last period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              Wearables Hub
            </h1>
            <p className="text-gray-600">
              Connect your smartwatch and track your health metrics in real-time
            </p>
          </div>
          <Button 
            onClick={() => setShowConnectDialog(true)}
            className="bg-gradient-to-r from-teal-600 to-cyan-600"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Connect Device
          </Button>
        </div>

        {/* Connected Devices Bar */}
        {connectedDevices.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-6">
            {connectedDevices.map((device) => {
              const Icon = DEVICE_ICONS[device.device_type] || Watch;
              return (
                <div 
                  key={device.id}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${DEVICE_COLORS[device.device_type]} text-white`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{device.device_name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-white hover:bg-white/20"
                    onClick={() => syncDevice(device.device_type)}
                    disabled={syncing === device.device_type}
                  >
                    <RefreshCw className={`w-3 h-3 ${syncing === device.device_type ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/80 backdrop-blur">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {connectedDevices.length === 0 ? (
              <Card className="p-12 text-center">
                <Watch className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Devices Connected</h3>
                <p className="text-gray-500 mb-6">
                  Connect your smartwatch or fitness tracker to start monitoring your health metrics.
                </p>
                <Button onClick={() => setShowConnectDialog(true)}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Your First Device
                </Button>
              </Card>
            ) : (
              <>
                {/* Period Selector */}
                <div className="flex gap-2">
                  {['daily', 'weekly', 'monthly'].map((period) => (
                    <Button
                      key={period}
                      variant={selectedPeriod === period ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedPeriod(period)}
                      className={selectedPeriod === period ? 'bg-teal-600' : ''}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </Button>
                  ))}
                </div>

                {/* Health Score Card */}
                {analytics && analytics.health_score && (
                  <Card className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-teal-100 mb-1">Overall Health Score</p>
                          <div className="flex items-baseline gap-2">
                            <span className="text-5xl font-bold">{analytics.health_score}</span>
                            <span className="text-xl text-teal-100">/100</span>
                          </div>
                          <p className="text-teal-100 mt-2">
                            {getHealthScoreLabel(analytics.health_score)}
                          </p>
                        </div>
                        <div className="text-right">
                          <Progress 
                            value={analytics.health_score} 
                            className="w-32 h-3 bg-white/20"
                          />
                          <p className="text-xs text-teal-100 mt-2">
                            Based on {analytics.data_points} data points
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Metrics Grid */}
                {analytics?.analytics && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {analytics.analytics.heart_rate && (
                      <MetricCard
                        title="Avg Heart Rate"
                        value={analytics.analytics.heart_rate.average}
                        unit="bpm"
                        icon={Heart}
                        color="from-red-500 to-pink-600"
                        subtitle={`Resting: ${analytics.analytics.heart_rate.resting} bpm`}
                      />
                    )}
                    {analytics.analytics.steps && (
                      <MetricCard
                        title="Daily Steps"
                        value={analytics.analytics.steps.daily_average.toLocaleString()}
                        unit="avg"
                        icon={Footprints}
                        color="from-blue-500 to-indigo-600"
                        subtitle={`Total: ${analytics.analytics.steps.total.toLocaleString()}`}
                      />
                    )}
                    {analytics.analytics.sleep && (
                      <MetricCard
                        title="Avg Sleep"
                        value={analytics.analytics.sleep.average}
                        unit="hrs"
                        icon={Moon}
                        color="from-purple-500 to-violet-600"
                        subtitle={`Best: ${analytics.analytics.sleep.best_night}h`}
                      />
                    )}
                    {analytics.analytics.calories && (
                      <MetricCard
                        title="Daily Calories"
                        value={analytics.analytics.calories.daily_average.toLocaleString()}
                        unit="kcal"
                        icon={Flame}
                        color="from-orange-500 to-red-600"
                        subtitle={`Total: ${analytics.analytics.calories.total.toLocaleString()}`}
                      />
                    )}
                    {analytics.analytics.hrv && (
                      <MetricCard
                        title="Avg HRV"
                        value={analytics.analytics.hrv.average}
                        unit="ms"
                        icon={Brain}
                        color="from-cyan-500 to-teal-600"
                        subtitle={`Trend: ${analytics.analytics.hrv.trend}`}
                      />
                    )}
                    {analytics.analytics.blood_oxygen && (
                      <MetricCard
                        title="Blood Oxygen"
                        value={analytics.analytics.blood_oxygen.average}
                        unit="%"
                        icon={Droplets}
                        color="from-sky-500 to-blue-600"
                        subtitle={`Range: ${analytics.analytics.blood_oxygen.min}-${analytics.analytics.blood_oxygen.max}%`}
                      />
                    )}
                    {analytics.analytics.active_minutes && (
                      <MetricCard
                        title="Active Minutes"
                        value={analytics.analytics.active_minutes.daily_average}
                        unit="min/day"
                        icon={Zap}
                        color="from-green-500 to-emerald-600"
                        subtitle={`Total: ${analytics.analytics.active_minutes.total} min`}
                      />
                    )}
                  </div>
                )}

                {/* Insights */}
                {analytics?.insights && analytics.insights.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-teal-600" />
                        Health Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analytics.insights.map((insight, index) => (
                          <div 
                            key={`item-${index}`}
                            className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                          >
                            <span className="text-lg">{insight.split(' ')[0]}</span>
                            <p className="text-gray-700">{insight.slice(insight.indexOf(' ') + 1)}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            {connectedDevices.length === 0 ? (
              <Card className="p-12 text-center">
                <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Data Yet</h3>
                <p className="text-gray-500">Connect a device to see your health trends.</p>
              </Card>
            ) : (
              <div className="space-y-8">
                {/* Device selector for trends */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-gray-600">View trends for:</span>
                  <Button
                    variant={selectedTrendDevice === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedTrendDevice('all')}
                    className={selectedTrendDevice === 'all' ? 'bg-teal-600' : ''}
                  >
                    All Devices Combined
                  </Button>
                  {connectedDevices.map((device) => {
                    const Icon = DEVICE_ICONS[device.device_type] || Watch;
                    return (
                      <Button
                        key={device.device_id}
                        variant={selectedTrendDevice === device.device_id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedTrendDevice(device.device_id)}
                        className={`gap-2 ${selectedTrendDevice === device.device_id ? `bg-gradient-to-r ${DEVICE_COLORS[device.device_type]}` : ''}`}
                      >
                        <Icon className="w-4 h-4" />
                        {device.device_name}
                      </Button>
                    );
                  })}
                </div>

                {/* Show combined or per-device trends */}
                {selectedTrendDevice === 'all' ? (
                  /* Combined trends from all devices */
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {trends.heart_rate && trends.heart_rate.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Heart className="w-5 h-5 text-red-500" />
                            Heart Rate Trend (All Devices)
                          </CardTitle>
                          <CardDescription>Average daily heart rate (30 days)</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={trends.heart_rate}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                              <YAxis domain={['dataMin - 5', 'dataMax + 5']} />
                              <Tooltip />
                              <Area type="monotone" dataKey="value" stroke="#ef4444" fill="#fecaca" name="BPM" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                    {trends.steps && trends.steps.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Footprints className="w-5 h-5 text-blue-500" />
                            Daily Steps (All Devices)
                          </CardTitle>
                          <CardDescription>Total steps per day (30 days)</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={trends.steps}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#3b82f6" name="Steps" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                    {trends.sleep && trends.sleep.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Moon className="w-5 h-5 text-purple-500" />
                            Sleep Duration (All Devices)
                          </CardTitle>
                          <CardDescription>Hours of sleep per night (30 days)</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={trends.sleep}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                              <YAxis domain={[0, 12]} />
                              <Tooltip />
                              <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="#ddd6fe" name="Hours" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                    {trends.hrv && trends.hrv.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Brain className="w-5 h-5 text-teal-500" />
                            Heart Rate Variability (All Devices)
                          </CardTitle>
                          <CardDescription>Daily HRV readings (30 days)</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={trends.hrv}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                              <YAxis />
                              <Tooltip />
                              <Line type="monotone" dataKey="value" stroke="#14b8a6" strokeWidth={2} dot={{ fill: '#14b8a6', r: 3 }} name="HRV (ms)" />
                            </LineChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ) : (
                  /* Per-device trends */
                  <div className="space-y-6">
                    {/* Device Header */}
                    {connectedDevices.filter(d => d.device_id === selectedTrendDevice).map((device) => {
                      const Icon = DEVICE_ICONS[device.device_type] || Watch;
                      const deviceColor = DEVICE_COLORS[device.device_type];
                      return (
                        <Card key={device.device_id} className={`bg-gradient-to-r ${deviceColor} text-white`}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <Icon className="w-8 h-8" />
                              <div>
                                <h3 className="text-lg font-semibold">{device.device_name} Trends</h3>
                                <p className="text-sm opacity-80">Last sync: {new Date(device.last_sync).toLocaleString()}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}

                    {/* Device-specific Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Heart Rate for selected device */}
                      {deviceTrends.heart_rate && deviceTrends.heart_rate
                        .filter(d => d.device_id === selectedTrendDevice)
                        .map((deviceData) => (
                          <Card key={`hr-${deviceData.device_id}`}>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <Heart className="w-5 h-5 text-red-500" />
                                Heart Rate - {deviceData.device_name}
                              </CardTitle>
                              <CardDescription>Average daily heart rate (30 days)</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={250}>
                                <AreaChart data={deviceData.trend}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                                  <YAxis domain={['dataMin - 5', 'dataMax + 5']} />
                                  <Tooltip />
                                  <Area type="monotone" dataKey="value" stroke="#ef4444" fill="#fecaca" name="BPM" />
                                </AreaChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        ))}

                      {/* Steps for selected device */}
                      {deviceTrends.steps && deviceTrends.steps
                        .filter(d => d.device_id === selectedTrendDevice)
                        .map((deviceData) => (
                          <Card key={`steps-${deviceData.device_id}`}>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <Footprints className="w-5 h-5 text-blue-500" />
                                Daily Steps - {deviceData.device_name}
                              </CardTitle>
                              <CardDescription>Total steps per day (30 days)</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={deviceData.trend}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                                  <YAxis />
                                  <Tooltip />
                                  <Bar dataKey="value" fill="#3b82f6" name="Steps" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        ))}

                      {/* Sleep for selected device */}
                      {deviceTrends.sleep && deviceTrends.sleep
                        .filter(d => d.device_id === selectedTrendDevice)
                        .map((deviceData) => (
                          <Card key={`sleep-${deviceData.device_id}`}>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <Moon className="w-5 h-5 text-purple-500" />
                                Sleep Duration - {deviceData.device_name}
                              </CardTitle>
                              <CardDescription>Hours of sleep per night (30 days)</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={250}>
                                <AreaChart data={deviceData.trend}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                                  <YAxis domain={[0, 12]} />
                                  <Tooltip />
                                  <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="#ddd6fe" name="Hours" />
                                </AreaChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        ))}

                      {/* HRV for selected device */}
                      {deviceTrends.hrv && deviceTrends.hrv
                        .filter(d => d.device_id === selectedTrendDevice)
                        .map((deviceData) => (
                          <Card key={`hrv-${deviceData.device_id}`}>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <Brain className="w-5 h-5 text-teal-500" />
                                Heart Rate Variability - {deviceData.device_name}
                              </CardTitle>
                              <CardDescription>Daily HRV readings (30 days)</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={250}>
                                <LineChart data={deviceData.trend}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                  <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(val) => val.slice(5)} />
                                  <YAxis />
                                  <Tooltip />
                                  <Line type="monotone" dataKey="value" stroke="#14b8a6" strokeWidth={2} dot={{ fill: '#14b8a6', r: 3 }} name="HRV (ms)" />
                                </LineChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        ))}
                    </div>

                    {/* Device-specific Analytics Summary */}
                    {deviceAnalytics?.devices?.filter(d => d.device_id === selectedTrendDevice).map((device) => (
                      <Card key={`analytics-${device.device_id}`}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Activity className="w-5 h-5 text-teal-600" />
                            {device.device_name} - {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} Summary
                          </CardTitle>
                          <CardDescription>Based on {device.data_points} data points</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {device.analytics.heart_rate && (
                              <div className="text-center p-3 bg-red-50 rounded-lg">
                                <Heart className="w-6 h-6 text-red-500 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-red-600">{device.analytics.heart_rate.average}</p>
                                <p className="text-xs text-gray-500">Avg HR (bpm)</p>
                              </div>
                            )}
                            {device.analytics.steps && (
                              <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <Footprints className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-blue-600">{device.analytics.steps.daily_average.toLocaleString()}</p>
                                <p className="text-xs text-gray-500">Avg Steps/day</p>
                              </div>
                            )}
                            {device.analytics.sleep && (
                              <div className="text-center p-3 bg-purple-50 rounded-lg">
                                <Moon className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-purple-600">{device.analytics.sleep.average}</p>
                                <p className="text-xs text-gray-500">Avg Sleep (hrs)</p>
                              </div>
                            )}
                            {device.analytics.hrv && (
                              <div className="text-center p-3 bg-teal-50 rounded-lg">
                                <Brain className="w-6 h-6 text-teal-500 mx-auto mb-1" />
                                <p className="text-2xl font-bold text-teal-600">{device.analytics.hrv.average}</p>
                                <p className="text-xs text-gray-500">Avg HRV (ms)</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {supportedDevices.map((device) => {
                const Icon = DEVICE_ICONS[device.id] || Watch;
                const isConnected = connectedDevices.some(d => d.device_type === device.id);
                const connectedDevice = connectedDevices.find(d => d.device_type === device.id);
                
                return (
                  <Card 
                    key={device.id}
                    className={`overflow-hidden transition-all ${
                      isConnected ? 'ring-2 ring-teal-500' : 'hover:shadow-lg'
                    }`}
                  >
                    <div className={`h-2 bg-gradient-to-r ${DEVICE_COLORS[device.id]}`} />
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${DEVICE_COLORS[device.id]}`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        {isConnected ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">
                            Available
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-semibold mb-1">{device.name}</h3>
                      
                      {isConnected && connectedDevice && (
                        <p className="text-xs text-gray-500 mb-4">
                          Last sync: {new Date(connectedDevice.last_sync).toLocaleString()}
                        </p>
                      )}
                      
                      <div className="flex gap-2 mt-4">
                        {isConnected ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => syncDevice(device.id)}
                              disabled={syncing === device.id}
                            >
                              {syncing === device.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-1" />
                              )}
                              Sync
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => disconnectDevice(device.id)}
                            >
                              <Unlink className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            className={`w-full bg-gradient-to-r ${DEVICE_COLORS[device.id]}`}
                            onClick={() => connectDevice(device.id)}
                            disabled={connecting === device.id}
                          >
                            {connecting === device.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Link2 className="w-4 h-4 mr-2" />
                            )}
                            Connect
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Integration Status Card */}
            <Card className={connectedDevices.some(d => d.provider !== 'mock') ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {connectedDevices.some(d => d.provider !== 'mock') ? (
                    <>
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-green-800 mb-1">Real Integration Active</h4>
                        <p className="text-green-700 text-sm">
                          Your wearable devices are connected via real API integrations. 
                          Health data is being synced directly from your devices.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-amber-800 mb-1">Demo Mode</h4>
                        <p className="text-amber-700 text-sm">
                          Currently using simulated data for demonstration. 
                          To connect real devices, configure one of these options in the backend:
                        </p>
                        <ul className="text-amber-700 text-sm mt-2 space-y-1">
                          <li>• <strong>Open Wearables</strong> (free, open-source) - <a href="https://github.com/the-momentum/open-wearables" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">github.com/the-momentum/open-wearables</a></li>
                          <li>• <strong>Fitbit Web API</strong> (free) - <a href="https://dev.fitbit.com/apps" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">dev.fitbit.com/apps</a></li>
                          <li>• <strong>Garmin Health API</strong> (free) - <a href="https://developer.garmin.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">developer.garmin.com</a></li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Connect Device Dialog */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Connect a Wearable Device</DialogTitle>
            <DialogDescription>
              Choose your device to start syncing health data
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            {supportedDevices.map((device) => {
              const Icon = DEVICE_ICONS[device.id] || Watch;
              const isConnected = connectedDevices.some(d => d.device_type === device.id);
              
              return (
                <button
                  key={device.id}
                  onClick={() => !isConnected && connectDevice(device.id)}
                  disabled={isConnected || connecting === device.id}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isConnected 
                      ? 'border-green-300 bg-green-50 cursor-not-allowed'
                      : 'border-gray-200 hover:border-teal-500 hover:shadow-md cursor-pointer'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${DEVICE_COLORS[device.id]} flex items-center justify-center mb-3`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-medium">{device.name}</h4>
                  {isConnected ? (
                    <span className="text-xs text-green-600">Connected</span>
                  ) : connecting === device.id ? (
                    <span className="text-xs text-teal-600 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Connecting...
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">Tap to connect</span>
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WearablesHub;
